import { Box, Typography } from "@mui/material";
import type { FC } from "react";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useAnalytics } from "../../../contexts/analytics/hooks";
import { INK } from "../../../theme/tokens";
import SystemDialog from "../../UIToolkit/Popups/Dialog";
import { getEvidenceLines, getPrimaryActionLabel, getReasonBody, getReasonTitle, getSecondaryActionLabel } from "./copy";
import { useApplyStatusDecision } from "./hooks";
import type { OpenStatusDecision } from "./types";
import { StatusDecisionChoice } from "./types";

type Props = {
	decision: OpenStatusDecision | null;
	departmentId: string;
	/** Display name of the nurse the decision is about, for the dialog body. */
	nurseName?: string;
	onClose: () => void;
};

/**
 * Two-action modal for an open HRIS status decision. Reused by both the Team
 * table and the nurse Profile page — do not fork. Renders the reason copy + the
 * supporting evidence (only non-null facts), and offers "apply the change"
 * (choice=changed) vs "keep the current status" (choice=kept).
 */
export const StatusDecisionDialog: FC<Props> = ({ decision, departmentId, nurseName, onClose }) => {
	const { t } = useTranslation();
	const { mutateAsync, isPending } = useApplyStatusDecision(departmentId);

	const handleApply = useCallback(
		async (choice: StatusDecisionChoice) => {
			if (!decision) {
				return;
			}
			await mutateAsync({
				decisionId: decision.id,
				choice,
				nurseId: decision.nurseId,
				reason: decision.reason,
				direction: decision.direction,
			});
			onClose();
		},
		[decision, mutateAsync, onClose],
	);

	if (!decision) {
		return null;
	}

	const evidenceLines = getEvidenceLines(t, decision);

	return (
		<SystemDialog
			dataTestId="status-decision-dialog"
			open={!!decision}
			onClose={onClose}
			title={getReasonTitle(t, decision.reason)}
			body={
				<Box sx={{ display: "flex", flexDirection: "column", gap: 2, minWidth: { xs: "auto", sm: 360 } }}>
					<Typography sx={{ color: INK.SECONDARY, fontSize: "14px", lineHeight: 1.5 }}>
						{nurseName ? `${nurseName}: ${getReasonBody(t, decision.reason)}` : getReasonBody(t, decision.reason)}
					</Typography>
					{evidenceLines.length > 0 && (
						<Box
							component="dl"
							sx={(theme) => ({
								m: 0,
								display: "grid",
								gridTemplateColumns: "auto 1fr",
								columnGap: 2,
								rowGap: 0.75,
								p: 1.5,
								borderRadius: "8px",
								backgroundColor: theme.palette.warning.light,
							})}
						>
							{evidenceLines.map((line) => (
								<Box key={line.label} sx={{ display: "contents" }}>
									<Typography component="dt" sx={{ color: INK.MUTED, fontSize: "13px", fontWeight: 600 }}>
										{line.label}
									</Typography>
									<Typography component="dd" sx={{ m: 0, color: INK.PRIMARY, fontSize: "13px" }}>
										{line.value}
									</Typography>
								</Box>
							))}
						</Box>
					)}
				</Box>
			}
			cancel={t("cancel")}
			confirm={getPrimaryActionLabel(t, decision.direction)}
			confirmButtonVariant="primary"
			confirmLoading={isPending}
			onConfirm={() => handleApply(StatusDecisionChoice.Changed)}
			secondaryAction={{
				label: getSecondaryActionLabel(t, decision.direction),
				onClick: () => handleApply(StatusDecisionChoice.Kept),
				loading: isPending,
				buttonVariant: "secondary",
			}}
		/>
	);
};

/**
 * Small hook that tracks the "decision opened" analytics event, kept here so
 * both call sites (Team table + Profile) emit the identical event.
 */
export function useTrackStatusDecisionOpened() {
	const { track } = useAnalytics();
	return useCallback(
		(decision: OpenStatusDecision, departmentId: string) => {
			track("team_status_decision_opened", {
				nurseId: decision.nurseId,
				reason: decision.reason,
				direction: decision.direction,
				department_id: departmentId,
			});
		},
		[track],
	);
}
